import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { db } from './firebase';
import { cachedGetDocs } from "./utils/firestoreCache";
import { Timestamp, doc, getDoc, setDoc } from 'firebase/firestore';
import SponsorshipDisplay from "./components/SponsorshipDisplay";
import { useSponsorship } from "./hooks/useSponsorship";
import { useAdmin } from "./hooks/useAdmin";
import { getDancerAgeCategoryForYear, shouldResultCountForRankingsInYear } from './utils/ageCategoryUtils';

const latinCategories = [
  "Solo Juvenile I", "Solo Juvenile II", "Solo Junior I",
  "Solo Junior II", "Solo Youth", "Solo Adult"
];

const contemporaryCategories = [
  "Solo Contemporary Juvenile I", "Solo Contemporary Juvenile II",
  "Solo Contemporary Junior I", "Solo Contemporary Junior II",
  "Solo Contemporary Youth", "Solo Contemporary Adult"
];

const hipHopCategories = [
  "Solo Hip Hop Juvenile I", "Solo Hip Hop Juvenile II",
  "Solo Hip Hop Junior I", "Solo Hip Hop Junior II",
  "Solo Hip Hop Youth", "Solo Hip Hop Adult"
];

const commercialCategories = [
  "Solo Commercial Juvenile I", "Solo Commercial Juvenile II",
  "Solo Commercial Junior I", "Solo Commercial Junior II",
  "Solo Commercial Youth", "Solo Commercial Adult"
];

export default function EternalCupWinners() {
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [winners, setWinners] = useState([]);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedDiscipline, setSelectedDiscipline] = useState("Latin");
  const [selectedCategory, setSelectedCategory] = useState("Solo Adult");
  const [error, setError] = useState(null);
  const [availableYears] = useState([2026, 2025, 2024, 2023]); // Years with rankings
  const { isSponsored } = useSponsorship();

  // Season status: "pending" = ongoing, "finished" = winners determined
  const [seasonStatuses, setSeasonStatuses] = useState({}); // { year: "pending"|"finished" }
  const [seasonStatusSaving, setSeasonStatusSaving] = useState(false);

  const currentYear = new Date().getFullYear();
  const isPendingYear = (year) => year >= currentYear;
  const selectedStatus = seasonStatuses[selectedYear] ?? (isPendingYear(selectedYear) ? 'pending' : 'finished');

  // Load season statuses from Firestore
  useEffect(() => {
    const loadSeasonStatuses = async () => {
      try {
        const snap = await getDoc(doc(db, 'adminSettings', 'seasonStatus'));
        if (snap.exists()) {
          setSeasonStatuses(snap.data());
        }
      } catch (e) {
        console.error('Failed to load season statuses:', e);
      }
    };
    loadSeasonStatuses();
  }, []);

  // Save season status for a year (admin only)
  const saveSeasonStatus = async (year, status) => {
    setSeasonStatusSaving(true);
    try {
      const newStatuses = { ...seasonStatuses, [year]: status };
      await setDoc(doc(db, 'adminSettings', 'seasonStatus'), newStatuses, { merge: true });
      setSeasonStatuses(newStatuses);
    } catch (e) {
      console.error('Failed to save season status:', e);
    } finally {
      setSeasonStatusSaving(false);
    }
  };

  // Get available categories based on selected discipline
  const getAvailableCategories = () => {
    switch (selectedDiscipline) {
      case "Latin":
        return latinCategories;
      case "Contemporary":
        return contemporaryCategories;
      case "Hip Hop":
        return hipHopCategories;
      case "Commercial":
        return commercialCategories;
      default:
        return [];
    }
  };

  // Calculate winners for selected year competitions (same logic as Eternal Cup Ranking)
  const calculateWinners = async () => {
    if (!selectedDiscipline || !selectedCategory || !selectedYear) {
      setWinners([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const yearStart = new Date(selectedYear, 0, 1); // January 1st of selected year
      const yearEnd = new Date(selectedYear + 1, 0, 1); // January 1st of next year

      const dancersMap = new Map();
      const dancerAgeCategories = {}; // Cache for age categories
      const allDancerNames = new Set(); // Collect all dancer names first

      // Step 1: Collect all dancer names from old structure competitions
      const oldQuery = ["competitions"];
      const oldSnapshot = await cachedGetDocs(...oldQuery);

      oldSnapshot.forEach((doc) => {
        const comp = doc.data();
        if (!comp.results || !comp.timestamp) return;

        const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
        
        // Only include competitions from selected year
        if (compDate < yearStart || compDate >= yearEnd) return;

        // Check if this competition matches our criteria
        const matchesDiscipline = comp.discipline === selectedDiscipline;
        const matchesCategory = comp.category === selectedCategory;
        
        if (!matchesDiscipline || !matchesCategory) return;

        // Collect dancer names
        if (Array.isArray(comp.results)) {
          comp.results.forEach((result) => {
            if (result.dancerName && !result.excluded) {
              allDancerNames.add(result.dancerName);
            }
          });
        }
      });

      // Step 2: Collect dancer names from new structure competitions
      const allDisciplinesSnapshot = await cachedGetDocs("disciplines");
      
      for (const disciplineDoc of allDisciplinesSnapshot.docs) {
        const disciplineName = disciplineDoc.id;
        if (disciplineName !== selectedDiscipline) continue;
        
        const categoriesSnapshot = await cachedGetDocs("disciplines", disciplineName, "categories");
        
        for (const categoryDoc of categoriesSnapshot.docs) {
          const categoryName = categoryDoc.id;
          if (categoryName !== selectedCategory) continue;
          
          try {
            // For Latin discipline, collect from dances subcollection
            if (selectedDiscipline === "Latin") {
              const dancesSnapshot = await cachedGetDocs("disciplines", disciplineName, "categories", categoryName, "dances");
              await Promise.all(dancesSnapshot.docs.map(d => cachedGetDocs("disciplines", disciplineName, "categories", categoryName, "dances", d.id, "competitions")));
              
              for (const danceDoc of dancesSnapshot.docs) {
                const danceName = danceDoc.id;
                
                const danceCompsQuery = ["disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"];
                const danceCompsSnapshot = await cachedGetDocs(...danceCompsQuery);
                
                danceCompsSnapshot.forEach((doc) => {
                  const comp = doc.data();
                  if (!comp.results || !comp.timestamp) return;

                  const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                  
                  // Only include competitions from selected year
                  if (compDate < yearStart || compDate >= yearEnd) return;

                  if (Array.isArray(comp.results)) {
                    comp.results.forEach((result) => {
                      if (result.dancerName && !result.excluded) {
                        allDancerNames.add(result.dancerName);
                      }
                    });
                  }
                });
              }
            } else {
              // For non-Latin disciplines, collect from direct competitions
              const directCompsQuery = ["disciplines", disciplineName, "categories", categoryName, "competitions"];
              const directCompsSnapshot = await cachedGetDocs(...directCompsQuery);
              
              directCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                if (!comp.results || !comp.timestamp) return;

                const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                
                // Only include competitions from selected year
                if (compDate < yearStart || compDate >= yearEnd) return;

                if (Array.isArray(comp.results)) {
                  comp.results.forEach((result) => {
                    if (result.dancerName && !result.excluded) {
                      allDancerNames.add(result.dancerName);
                    }
                  });
                }
              });
            }
          } catch (error) {
            console.log("🔍 No competitions found for", disciplineName, categoryName);
          }
        }
      }

      // Step 3: Get age categories for all collected dancers for the selected year
      console.log(`🎂 Getting age categories for ${allDancerNames.size} dancers for year ${selectedYear}`);
      await Promise.all(
        Array.from(allDancerNames).map(async (name) => {
          dancerAgeCategories[name] = await getDancerAgeCategoryForYear(name, selectedYear);
        })
      );

      // Step 4: Process results with age category filtering (old structure)
      oldSnapshot.forEach((doc) => {
        const comp = doc.data();
        if (!comp.results || !comp.timestamp) return;

        const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
        
        // Only include competitions from selected year
        if (compDate < yearStart || compDate >= yearEnd) return;

        // Check if this competition matches our criteria
        const matchesDiscipline = comp.discipline === selectedDiscipline;
        const matchesCategory = comp.category === selectedCategory;
        
        if (!matchesDiscipline || !matchesCategory) return;

        // For Latin discipline, we combine all dances
        if (selectedDiscipline === "Latin") {
          // Process all Latin dances for this category
          comp.results.forEach((result) => {
            // Skip excluded results, but only for current year (manual exclusions should not affect historical rankings)
            const currentYear = new Date().getFullYear();
            const shouldSkipDueToExclusion = result.excluded && selectedYear === currentYear;
            if (shouldSkipDueToExclusion) return;
            
            const dancerName = result.dancerName;
            
            // Check if this result should count for rankings based on age category for the selected year
            const ageCategoryInYear = dancerAgeCategories[dancerName] ?? null;
            if (!shouldResultCountForRankingsInYear(selectedCategory, ageCategoryInYear)) {
              return;
            }
            
            if (!dancersMap.has(dancerName)) {
              dancersMap.set(dancerName, {
                name: dancerName,
                studio: result.studio || "-",
                results: [],
                totalPoints: 0
              });
            }

            const score = Math.round((result.points || 0) * (comp.multiplier || 1));
            dancersMap.get(dancerName).results.push({
              compId: doc.id,
              compName: comp.name || doc.id,
              dance: comp.dance || "Unknown",
              points: score,
              place: result.place,
              timestamp: comp.timestamp,
              participants: comp.participants || 0
            });
          });
        } else {
          // For non-Latin disciplines, process normally
          comp.results.forEach((result) => {
            // Skip excluded results, but only for current year (manual exclusions should not affect historical rankings)
            const currentYear = new Date().getFullYear();
            const shouldSkipDueToExclusion = result.excluded && selectedYear === currentYear;
            if (shouldSkipDueToExclusion) return;
            
            const dancerName = result.dancerName;
            
            // Check if this result should count for rankings based on age category for the selected year
            const ageCategoryInYear = dancerAgeCategories[dancerName] ?? null;
            if (!shouldResultCountForRankingsInYear(selectedCategory, ageCategoryInYear)) {
              return;
            }
            
            if (!dancersMap.has(dancerName)) {
              dancersMap.set(dancerName, {
                name: dancerName,
                studio: result.studio || "-",
                results: [],
                totalPoints: 0
              });
            }

            const score = Math.round((result.points || 0) * (comp.multiplier || 1));
            dancersMap.get(dancerName).results.push({
              compId: doc.id,
              compName: comp.name || doc.id,
              points: score,
              place: result.place,
              timestamp: comp.timestamp,
              participants: comp.participants || 0
            });
          });
        }
      });

      // Query new structure competitions
      const disciplinesSnapshot = await cachedGetDocs("disciplines");
      
      for (const disciplineDoc of disciplinesSnapshot.docs) {
        const disciplineName = disciplineDoc.id;
        if (disciplineName.toLowerCase().replace(/[^a-z0-9]+/g, "_") !== selectedDiscipline.toLowerCase().replace(/[^a-z0-9]+/g, "_")) continue;

        const categoriesSnapshot = await cachedGetDocs("disciplines", disciplineName, "categories");
        
        for (const categoryDoc of categoriesSnapshot.docs) {
          const categoryName = categoryDoc.id;
          const originalCategory = selectedCategory.split(" ").slice(-2).join("_").toLowerCase();
          if (categoryName !== originalCategory) continue;

          // For Latin discipline, query all dances and combine results
          if (selectedDiscipline === "Latin") {
            try {
              const dancesSnapshot = await cachedGetDocs("disciplines", disciplineName, "categories", categoryName, "dances");
              await Promise.all(dancesSnapshot.docs.map(d => cachedGetDocs("disciplines", disciplineName, "categories", categoryName, "dances", d.id, "competitions")));
              
              for (const danceDoc of dancesSnapshot.docs) {
                const danceName = danceDoc.id;
                
                const danceCompsQuery = ["disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"];
                const danceCompsSnapshot = await cachedGetDocs(...danceCompsQuery);
                
                danceCompsSnapshot.forEach((doc) => {
                  const comp = doc.data();
                  if (!comp.results || !comp.timestamp) return;

                  const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                  
                  // Only include competitions from selected year
                  if (compDate < yearStart || compDate >= yearEnd) return;

                  comp.results.forEach((result) => {
                    // Skip excluded results, but only for current year (manual exclusions should not affect historical rankings)
                    const currentYear = new Date().getFullYear();
                    const shouldSkipDueToExclusion = result.excluded && selectedYear === currentYear;
                    if (shouldSkipDueToExclusion) return;
                    
                    const dancerName = result.dancerName;
                    
                    // Check if this result should count for rankings based on age category for the selected year
                    const ageCategoryInYear = dancerAgeCategories[dancerName] ?? null;
                    if (!shouldResultCountForRankingsInYear(selectedCategory, ageCategoryInYear)) {
                      return;
                    }
                    
                    if (!dancersMap.has(dancerName)) {
                      dancersMap.set(dancerName, {
                        name: dancerName,
                        studio: result.studio || "-",
                        results: [],
                        totalPoints: 0
                      });
                    }

                    const score = Math.round((result.points || 0) * (comp.multiplier || 1));
                    dancersMap.get(dancerName).results.push({
                      compId: doc.id,
                      compName: comp.name || doc.id,
                      dance: danceName,
                      points: score,
                      place: result.place,
                      timestamp: comp.timestamp,
                      participants: comp.participants || 0
                    });
                  });
                });
              }
            } catch (error) {
              console.log("🔍 No dances found for", disciplineName, categoryName);
            }
          } else {
            // For non-Latin disciplines, query direct competitions
            try {
              const directCompsQuery = ["disciplines", disciplineName, "categories", categoryName, "competitions"];
              const directCompsSnapshot = await cachedGetDocs(...directCompsQuery);
              
              directCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                if (!comp.results || !comp.timestamp) return;

                const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                
                // Only include competitions from selected year
                if (compDate < yearStart || compDate >= yearEnd) return;

                comp.results.forEach((result) => {
                  // Skip excluded results, but only for current year (manual exclusions should not affect historical rankings)
                  const currentYear = new Date().getFullYear();
                  const shouldSkipDueToExclusion = result.excluded && selectedYear === currentYear;
                  if (shouldSkipDueToExclusion) return;
                  
                  const dancerName = result.dancerName;
                  
                  // Check if this result should count for rankings based on age category for the selected year
                  const ageCategoryInYear = dancerAgeCategories[dancerName] ?? null;
                  if (!shouldResultCountForRankingsInYear(selectedCategory, ageCategoryInYear)) {
                    return;
                  }
                  
                  if (!dancersMap.has(dancerName)) {
                    dancersMap.set(dancerName, {
                      name: dancerName,
                      studio: result.studio || "-",
                      results: [],
                      totalPoints: 0
                    });
                  }

                  const score = Math.round((result.points || 0) * (comp.multiplier || 1));
                  dancersMap.get(dancerName).results.push({
                    compId: doc.id,
                    compName: comp.name || doc.id,
                    points: score,
                    place: result.place,
                    timestamp: comp.timestamp,
                    participants: comp.participants || 0
                  });
                });
              });
            } catch (error) {
              console.log("🔍 No direct competitions for", disciplineName, categoryName);
            }
          }
        }
      }

      // Calculate total points for each dancer (same logic as Ranking page)
      const topN = selectedYear >= 2026 ? 6 : 4; // 2026+ uses top 6, earlier years top 4
      const dancersWithPoints = Array.from(dancersMap.values()).map((dancer) => {
        if (selectedDiscipline === "Latin") {
          // For Latin: topN results from each dance, combined together
          const danceResults = {};
          
          // Group results by dance
          dancer.results.forEach((result) => {
            const dance = result.dance || "Unknown";
            if (!danceResults[dance]) {
              danceResults[dance] = [];
            }
            danceResults[dance].push(result);
          });
          
          // Get topN results from each dance and combine them
          let allTopResults = [];
          Object.entries(danceResults).forEach(([dance, results]) => {
            const sortedDanceResults = results.sort((a, b) => b.points - a.points);
            const topFromDance = sortedDanceResults.slice(0, topN);
            allTopResults = allTopResults.concat(topFromDance);
          });
          
          // Sort all combined results and calculate total points
          allTopResults.sort((a, b) => b.points - a.points);
          const totalPoints = allTopResults.reduce((sum, result) => sum + result.points, 0);
          const danceCount = Object.keys(danceResults).length;
          
          return {
            ...dancer,
            totalPoints,
            competitionCount: dancer.results.length,
            danceCount,
            bestResults: allTopResults,
            resultCount: allTopResults.length
          };
        } else {
          // For non-Latin: best topN results
          const sortedResults = dancer.results.sort((a, b) => b.points - a.points);
          const topResults = sortedResults.slice(0, topN);
          const totalPoints = topResults.reduce((sum, result) => sum + result.points, 0);
          
          return {
            ...dancer,
            totalPoints,
            competitionCount: dancer.results.length,
            bestResults: topResults,
            resultCount: topResults.length
          };
        }
      });

      // Sort by total points and get top 3 winners (with tie handling)
      const sortedByPoints = dancersWithPoints
        .filter(dancer => dancer.totalPoints > 0)
        .sort((a, b) => b.totalPoints - a.totalPoints);
      
      // Assign ranks with proper tie handling
      let lastPoints = null;
      let lastRank = 0;
      let tieCount = 0;
      
      const rankedDancers = sortedByPoints.map((dancer) => {
        if (lastPoints === dancer.totalPoints) {
          // Same points as previous dancer = same rank
          tieCount++;
          return { ...dancer, rank: lastRank };
        } else {
          // Different points = new rank (accounting for ties)
          const newRank = lastRank + tieCount + 1;
          lastRank = newRank;
          lastPoints = dancer.totalPoints;
          tieCount = 0;
          return { ...dancer, rank: newRank };
        }
      });
      
      // Get all dancers with rank <= 3 (includes ties in top 3)
      const sortedDancers = rankedDancers.filter(dancer => dancer.rank <= 3);

      console.log(`🏆 Found ${sortedDancers.length} winners for ${selectedDiscipline} - ${selectedCategory} (${selectedYear})`);
      setWinners(sortedDancers);

    } catch (error) {
      console.error("❌ Error calculating Eternal Cup Winners:", error);
      setError(`Error loading winners: ${error.message}`);
      setWinners([]);
    } finally {
      setLoading(false);
    }
  };

  // Load cached winners data once on component mount
  useEffect(() => {
    calculateWinners();
  }, [selectedDiscipline, selectedCategory, selectedYear]);

  // Process winners for current selection from live calculation
  useEffect(() => {
    // This effect is handled by calculateWinners above
  }, []);

  // Handle studio name click
  const handleStudioClick = (studioName) => {
    navigate(`/studio/${encodeURIComponent(studioName)}`);
  };

  // Handle dancer name click
  const handleDancerClick = (dancerName) => {
    navigate(`/dancer/${encodeURIComponent(dancerName)}`);
  };

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>🏆 Eternal Cup Winners</h2>
        <div>🔄 Loading winners...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>🏆 Eternal Cup Winners</h2>
        <div style={{ color: "red" }}>❌ Error: {String(error)}</div>
      </div>
    );
  }

  return (
    <div style={{ padding: "1rem", maxWidth: "1200px", margin: "0 auto" }}>
      <h2>🏆 Eternal Cup Winners {selectedYear}</h2>
      <p style={{ fontSize: "1.1em", color: "#666", marginBottom: "2rem" }}>
        <strong>Hall of Fame:</strong> Top 3 winners in each category for completed seasons.<br/>
        <strong>Points Logic:</strong> {selectedDiscipline === "Latin" 
          ? (selectedYear >= 2026
            ? `Top 6 results from each of the 5 dances combined together (up to 30 total results).`
            : `Top 4 results from each of the 5 dances combined together (up to 20 total results).`)
          : (selectedYear >= 2026
            ? "Best 6 results from selected year competitions."
            : "Best 4 results from selected year competitions.")
        }
      </p>

      {/* Admin season status toggle — only for current year and future */}
      {isAdmin && isPendingYear(selectedYear) && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem", padding: "0.6rem 1rem", background: "#fff3cd", border: "1px solid #ffc107", borderRadius: "8px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.82rem", fontWeight: "600", color: "#856404" }}>⚙️ Admin — Season {selectedYear}:</span>
          <div style={{ display: "flex", gap: "0.4rem" }}>
            {['pending', 'finished'].map(status => (
              <button
                key={status}
                disabled={seasonStatusSaving}
                onClick={() => saveSeasonStatus(selectedYear, status)}
                style={{
                  padding: "3px 12px",
                  fontSize: "0.78rem",
                  fontWeight: "600",
                  borderRadius: "20px",
                  border: selectedStatus === status ? "2px solid transparent" : "2px solid #ccc",
                  cursor: seasonStatusSaving ? "not-allowed" : "pointer",
                  background: selectedStatus === status
                    ? (status === 'pending' ? '#e67e22' : '#27ae60')
                    : '#fff',
                  color: selectedStatus === status ? '#fff' : '#555',
                  transition: "all 0.15s"
                }}
              >
                {status === 'pending' ? '⏳ Pending' : '✅ Finished'}
              </button>
            ))}
          </div>
          {seasonStatusSaving && <span style={{ fontSize: "0.75rem", color: "#888" }}>Saving…</span>}
        </div>
      )}

      {/* Pending season banner — visible to all users */}
      {selectedStatus === 'pending' && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", marginBottom: "1.5rem", padding: "1rem 1.25rem", background: "linear-gradient(135deg, #fff8e1, #fff3cd)", border: "2px solid #ffc107", borderRadius: "10px", boxShadow: "0 2px 8px rgba(255,193,7,0.2)" }}>
          <span style={{ fontSize: "1.6rem", lineHeight: 1 }}>⏳</span>
          <div>
            <div style={{ fontWeight: "700", fontSize: "1rem", color: "#856404", marginBottom: "0.3rem" }}>Season {selectedYear} is still ongoing</div>
            <div style={{ fontSize: "0.9rem", color: "#6d5300", lineHeight: "1.5" }}>
              The winners will be determined at the end of the last competition of {selectedYear}. Check back then for the final results!
            </div>
          </div>
        </div>
      )}

      {/* Year, Discipline, and Category Selection */}
      <div style={{ 
        display: "flex", 
        gap: "1rem", 
        marginBottom: "2rem", 
        padding: "1rem", 
        backgroundColor: "#f5f5f5", 
        borderRadius: "5px",
        border: "1px solid #ddd",
        flexWrap: "wrap"
      }}>
        <label style={{ display: "flex", flexDirection: "column", minWidth: "180px" }}>
          <strong>Year:</strong>
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{ padding: "0.5rem", marginTop: "0.25rem" }}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </label>
        
        <label style={{ display: "flex", flexDirection: "column", minWidth: "180px" }}>
          <strong>Discipline:</strong>
          <select 
            value={selectedDiscipline} 
            onChange={(e) => {
              setSelectedDiscipline(e.target.value);
              setSelectedCategory(""); // Reset category when discipline changes
            }}
            style={{ padding: "0.5rem", marginTop: "0.25rem" }}
          >
            <option value="Latin">Latin</option>
            <option value="Contemporary">Contemporary</option>
            <option value="Hip Hop">Hip Hop</option>
            <option value="Commercial">Commercial</option>
          </select>
        </label>
        
        <label style={{ display: "flex", flexDirection: "column", minWidth: "180px" }}>
          <strong>Age Category:</strong>
          <select 
            value={selectedCategory} 
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ padding: "0.5rem", marginTop: "0.25rem" }}
          >
            <option value="">Select Category</option>
            {getAvailableCategories().map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ 
          color: "#d32f2f", 
          backgroundColor: "#ffebee", 
          padding: "1rem", 
          borderRadius: "5px", 
          marginBottom: "1rem" 
        }}>
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>Loading winners...</p>
        </div>
      )}

      {/* No Selection State */}
      {!selectedDiscipline || !selectedCategory || !selectedYear ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
          <p>Please select year, discipline and category to view winners.</p>
        </div>
      ) : null}

      {/* Winners Display */}
      {!loading && selectedDiscipline && selectedCategory && selectedYear && winners.length > 0 && (
        <div>
          <h3>🏆 Top 3 Winners for {selectedDiscipline} - {selectedCategory} ({selectedYear})</h3>
          <p style={{ fontSize: "0.9em", color: "#666", marginBottom: "1rem" }}>
            Showing top {winners.length} winners with total points from {selectedYear} competitions
            {winners.length > 3 && " (includes ties)"}
          </p>
          
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", gap: "1.5rem" }}>
            {winners.map((winner, index) => {
              if (!winner || typeof winner !== 'object') return null;
              
              // Use winner.rank for medal colors (handles ties properly)
              const medalColors = {
                1: { border: "#ffd700", bg: "#fffbf0", emoji: "🥇", title: "Champion" },
                2: { border: "#c0c0c0", bg: "#f8f8f8", emoji: "🥈", title: "2nd Place" },
                3: { border: "#cd7f32", bg: "#faf7f4", emoji: "🥉", title: "3rd Place" }
              };
              
              const colors = medalColors[winner.rank] || { border: "#ddd", bg: "#fff", emoji: "🏅", title: "Winner" };
              
              return (
                <div
                  key={`${winner.name}-${index}`}
                  style={{
                    position: "relative",
                    padding: "2rem",
                    border: `3px solid ${colors.border}`,
                    borderRadius: "15px",
                    backgroundColor: colors.bg,
                    textAlign: "center",
                    boxShadow: "0 6px 12px rgba(0,0,0,0.1)",
                    transition: "transform 0.2s ease",
                  }}
                  onMouseEnter={(e) => e.target.style.transform = "translateY(-5px)"}
                  onMouseLeave={(e) => e.target.style.transform = "translateY(0)"}
                >
                  {/* Sponsorship Display - Top Right Corner */}
                  <SponsorshipDisplay 
                    dancerName={winner.name}
                    isSponsored={isSponsored(winner.name)}
                    size="small"
                    layout="horizontal"
                    position="card-corner"
                  />
                  
                  <div style={{ fontSize: "4em", marginBottom: "0.5rem" }}>
                    {colors.emoji}
                  </div>
                  
                  <Link 
                    to={`/dancer/${encodeURIComponent(winner.name)}`}
                    style={{ 
                      fontSize: "1.6em", 
                      fontWeight: "bold", 
                      marginBottom: "0.5rem",
                      color: "#0066cc",
                      cursor: "pointer",
                      textDecoration: "underline",
                      display: "block"
                    }}
                  >
                    {String(winner.name || "Unknown")}
                  </Link>
                  
                  {winner.studio && (
                    <Link 
                      to={`/studio/${encodeURIComponent(winner.studio)}`}
                      style={{ 
                        fontSize: "1.1em", 
                        color: "#0066cc",
                        marginBottom: "0.5rem",
                        cursor: "pointer",
                        textDecoration: "underline",
                        display: "block"
                      }}
                    >
                      📍 {String(winner.studio)}
                    </Link>
                  )}
                  
                  <div style={{ 
                    fontSize: "1.4em", 
                    fontWeight: "bold",
                    color: colors.border === "#ffd700" ? "#b8860b" : 
                          colors.border === "#c0c0c0" ? "#708090" : "#8b4513",
                    marginBottom: "0.5rem"
                  }}>
                    {colors.title}
                  </div>
                  
                  <div style={{ 
                    fontSize: "1.8em", 
                    fontWeight: "bold",
                    color: "#333",
                    padding: "0.5rem",
                    backgroundColor: "rgba(255,255,255,0.8)",
                    borderRadius: "8px",
                    border: "2px solid #eee"
                  }}>
                    {index === 0 ? "🏆 " : ""}{winner.totalPoints || 0} pts
                  </div>
                  
                  <div style={{ 
                    fontSize: "0.9em", 
                    color: "#666",
                    marginTop: "0.5rem"
                  }}>
                    {selectedDiscipline === "Latin" 
                      ? `${winner.danceCount || 5}/5 dances (${winner.resultCount || 0} results)` 
                      : `${winner.resultCount || 0} results`
                    }
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Results State */}
      {!loading && selectedDiscipline && selectedCategory && selectedYear && winners.length === 0 && (
        <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
          <p>No winners found for {selectedDiscipline} - {selectedCategory} in {selectedYear}.</p>
          <p style={{ fontSize: "0.9em", marginTop: "1rem" }}>
            This could mean no competitions were held for this category in {selectedYear}, or no dancers qualified with sufficient points.
          </p>
        </div>
      )}
    </div>
  );
}
